import { CreditCard } from "account/_shared/models/CreditCard";
import AddCardWithElements from "account/payment/components/AddCardModal/AddCardModal";
import { API } from "aws-amplify";
import { useUserPanels } from "dashboard/_root/contexts/UserPanelsContext";
import { useEffect, useState } from "react";
import Button from "_shared/components/Button/Button";
import CustomModal from "_shared/components/Modal/Modal";
import { useModal } from "_shared/contexts/ModalContext";
import { useUserSubscriptionsQuery } from "_shared/graphql";
import styles from "./UpsellModal.module.scss";
import { analytics } from "_shared/utils/analytics";

interface Props {
  upgradeToPlan?: string;
  upgradeToPanel?: string;
  upgradeToAddon?: string;
}

const UpsellModal = ({ upgradeToPlan, upgradeToPanel, upgradeToAddon }: Props) => {
  const [open, setOpen] = useState(!!upgradeToPlan);
  const [loading, setLoading] = useState(false);
  const [addCardModalOpen, setAddCardModalOpen] = useState(false);
  const [defaultCard, setDefaultCard] = useState<CreditCard>();
  const { onErrorMsg, onSuccessMsg } = useModal();
  const { fetchPanels } = useUserPanels();
  const { data: userSubsData, refetch: refetchSubs } = useUserSubscriptionsQuery();

  const [selectedSubscription, setSelectedSubscription] =
    useState<Record<string, any>>();
  const [selectedAddon, setSelectedAddon] =
    useState<Record<string, any>>();
  const [currentSubscription, setCurrentSubscription] =
    useState<Record<string, any>>();
  const reqBody = {
    addedPriceIds: [selectedSubscription?.monthly_daily_price_id ?? selectedAddon?.monthly_daily_price_id],
    removedPriceIds: upgradeToPlan ? [currentSubscription?.price_id] : [],
  };
  const [proratedAmount, setProratedAmount] = useState<number>();

  useEffect(() => {
    const getDefaultCard = async () => {
      const cardList = await API.get("auth", "/cognito/listCards", {});
      const defaultCard = cardList.find((card: CreditCard) => card.default);
      setDefaultCard(defaultCard);
    };
    getDefaultCard();
  }, []);

  useEffect(() => {
    if (!userSubsData) return;
    const currentPlan = userSubsData?.user_subscriptions.find((sub) =>
      sub.subscription_id.endsWith("plan"),
    );
    setCurrentSubscription(currentPlan);
    // subs sorted by price asc
    const subscriptions = userSubsData?.subscriptions.slice().sort((a, b) => {
      return a.monthly_daily_price - b.monthly_daily_price;
    });

    if (upgradeToPanel) {
      // find cheapest plan that includes the panel
      const sub = subscriptions.find((sub) =>
        sub.longbow_tabs?.includes(upgradeToPanel),
      );
      setSelectedSubscription(sub);
    } else if (upgradeToPlan) {
      // find specific plan to upgrade to
      const sub = subscriptions.find((sub) => sub.id === upgradeToPlan);
      setSelectedSubscription(sub);
    } else if (upgradeToAddon) {
      console.log(upgradeToAddon);
      const sub = subscriptions.find((sub) =>
        sub.id === upgradeToAddon,
      );
      setSelectedAddon(sub);
    }
  }, [userSubsData]);

  useEffect(() => {
    if (!selectedSubscription && !selectedAddon) return;
    const getProratedEstimate = async () => {
      const estimate = await API.post("auth", "/cognito/estimateSubscription", {
        body: reqBody,
      });
      setProratedAmount(estimate);
    };
    getProratedEstimate();
  }, [selectedSubscription, selectedAddon]);

  const upgrade = async () => {
    setLoading(true);
    const res = await API.post("auth", "/cognito/changeSubscription", {
      body: reqBody,
    });
    if (res?.code === "Success") {
      analytics.track("Subscription Upgraded", {
        panel: upgradeToPanel,
        subscription: upgradeToPlan,
        addon: upgradeToAddon,
      });
      fetchPanels(); // refresh panels
      onSuccessMsg(
        `You now have access to ${upgradeToAddon ? selectedAddon?.name : upgradeToPanel ??
          selectedSubscription?.total_tickers + " more tickers"
        }.`,
        () => {
          setOpen(false);
        },
      );
      refetchSubs();
    } else {
      onErrorMsg(
        typeof res?.message == "string" ? res.message : "Something went wrong.",
      );
    }
    setLoading(false);
  };

  return (
    <>
      {upgradeToPanel ? (
        <article className={styles.panelModalPrompt}>
          <div>
            Sorry, you do not have access to this panel with your current plan.
          </div>
          <Button onClick={() => setOpen(true)}>
            Click here to upgrade now
          </Button>
        </article>
      ) : null}
      {upgradeToAddon ? (
        <article className={styles.panelModalPrompt}>
          <div>
            Sorry, you do not have access to the "{selectedAddon?.name ?? '______'}" add-on.
          </div>
          <Button onClick={() => setOpen(true)}>
            Click here to purchase this add-on
          </Button>
        </article>
      ) : null}
      <CustomModal open={open} onClose={() => setOpen(false)}>
        <div className={styles.container}>
          <i className={"fas fa-wallet"} />
          <div>
            <p>
              {upgradeToAddon ? `To view this panel, you must first purchase the "${selectedAddon?.name}" add-on.` : `To access ${upgradeToPanel ??
                selectedSubscription?.total_tickers + " more tickers"
                }, you must upgrade to the ${selectedSubscription?.name || "Test Plan"
                }.`}
            </p>
            {defaultCard?.last4 ? (
              <p>{`You will be charged a prorated amount of $${proratedAmount} today using the card ending in ${defaultCard?.last4}.`}</p>
            ) : (
              <>
                <p>{`You will be charged $${selectedSubscription?.monthly_daily_price ?? selectedAddon?.monthly_daily_price
                  } a month.`}</p>
              </>
            )}
          </div>
          {defaultCard?.last4 ? (
            <Button
              disabled={(!selectedSubscription && !selectedAddon) || !defaultCard}
              onClick={upgrade}
              loading={loading}
              fullWidth
            >
              Upgrade
            </Button>
          ) : (
            <Button
              onClick={() => setAddCardModalOpen(true)}
              loading={loading}
              fullWidth
            >
              Add Card & Upgrade
            </Button>
          )}
        </div>
      </CustomModal>
      <AddCardWithElements
        open={addCardModalOpen}
        onClose={() => setAddCardModalOpen(false)}
        onSuccess={() => {
          setAddCardModalOpen(false);
          upgrade();
        }}
      />
    </>
  );
};

export default UpsellModal;
